import React, { useEffect, useState } from 'react';
import { Layout, Typography, Button } from 'antd';
import {
  ApiOutlined,
  AppstoreOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  BookOutlined,
  CheckCircleFilled,
  DesktopOutlined,
  EyeOutlined,
  LineChartOutlined,
  QrcodeOutlined,
  RobotOutlined,
  ScheduleOutlined,
  SoundOutlined,
  TrophyOutlined,
  WechatOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import PublicHeader from '../components/layout/PublicHeader';
import PublicFooter from '../components/layout/PublicFooter';
import '../assets/css/HomePage.css';

const { Content } = Layout;
const { Paragraph, Text } = Typography;

const SLIDES = [
  {
    id: 'overview',
    navLabel: '产品概览',
    eyebrow: '课堂管理一体化平台',
    title: '教师必备免费工具--教室小喇叭',
    description:
      '教室小喇叭把课堂提醒、作业收取、积分激励、成长沉淀和数据统计串成一条连续体验，教师不用在多个系统之间来回切换。',
    value: '一屏总览',
    valueLabel: '从课堂提醒到班级运营',
    highlights: [
      '围绕教师日常高频场景设计，不是零散工具堆叠',
      '后台、小程序、教室桌面客户端三端协同',
      '适合班主任、任课教师和学校管理者共同使用'
    ],
    accent: '#61d0ff',
    accentSoft: 'rgba(97, 208, 255, 0.22)',
    icon: <AppstoreOutlined />
  },
  {
    id: 'broadcast',
    navLabel: '实时广播',
    eyebrow: '核心能力 01',
    title: '实时广播，把通知直接送到教室现场',
    description:
      '教师可在微信小程序或后台快速发起广播，消息实时送达教室桌面端，支持文字提醒、语音播报和多种显示方式。',
    value: '智能广播',
    valueLabel: '通知、提醒、临时调度统一入口',
    highlights: [
      '支持顶部横幅、全屏提示、弹窗展示等课堂场景',
      '适合上课提醒、临时通知、值日安排和流程切换',
      '桌面客户端即时接收，让广播真正发生在教室里'
    ],
    accent: '#4ca8ff',
    accentSoft: 'rgba(76, 168, 255, 0.22)',
    icon: <SoundOutlined />
  },
  {
    id: 'ai',
    navLabel: 'AI 助手',
    eyebrow: '核心能力 02',
    title: 'AI 助手，把教师高频动作做成可复用能力',
    description:
      '从个人 AI 助手到学校 AI 算力池，再到会员中心配置，系统把 AI 从“能用”做成“可管理、可分配、可持续使用”的教学能力。',
    value: 'AI 赋能',
    valueLabel: '个人助手 + 学校算力池 + 权益管理',
    highlights: [
      '支持教师个人模型设置和学校统一算力配置',
      '围绕课堂辅助、作业分析、日常处理等任务展开',
      '套餐、Token、权限和使用统计形成完整闭环'
    ],
    accent: '#7f8cff',
    accentSoft: 'rgba(127, 140, 255, 0.24)',
    icon: <RobotOutlined />
  },
  {
    id: 'growth',
    navLabel: '成长记录',
    eyebrow: '核心能力 03',
    title: '学生成长记录，让过程性管理真正沉淀下来',
    description:
      '把表扬、违纪、家访、日常观察和班级表现沉淀到同一份成长档案里，形成能长期追踪的学生画像。',
    value: '成长档案',
    valueLabel: '表扬、家访、行为记录持续积累',
    highlights: [
      '把碎片化班级记录整理成可回看的过程数据',
      '帮助班主任追踪学生变化与沟通节点',
      '和积分、成绩、班级管理结合成完整评价视角'
    ],
    accent: '#57d7b6',
    accentSoft: 'rgba(87, 215, 182, 0.24)',
    icon: <BookOutlined />
  },
  {
    id: 'pet',
    navLabel: '宠物积分',
    eyebrow: '核心能力 04',
    title: '宠物积分，把班级激励做得更有参与感',
    description:
      '积分管理、宠物中心和宠物排行把原本枯燥的班级激励转成学生愿意参与的成长反馈，让日常管理更有互动性。',
    value: '激励体系',
    valueLabel: '积分兑换 + 宠物成长 + 排行反馈',
    highlights: [
      '把日常表现和课堂参与转化成看得见的激励',
      '通过宠物成长和排行提升学生持续参与度',
      '适合班级文化建设和长期习惯培养'
    ],
    accent: '#ffb347',
    accentSoft: 'rgba(255, 179, 71, 0.24)',
    icon: <TrophyOutlined />
  },
  {
    id: 'grades',
    navLabel: '成绩管理',
    eyebrow: '核心能力 05',
    title: '成绩管理，让结果统计和过程评价放在一起看',
    description:
      '成绩管理不只用于录入分数，更适合和成长记录、班级表现、课堂数据一起看，帮助教师更快发现学生变化。',
    value: '结果分析',
    valueLabel: '成绩录入、分析和班级对比',
    highlights: [
      '支持成绩录入、班级查看和结果整理',
      '和成长记录搭配，更容易看清学生状态变化',
      '为家校沟通、班级复盘和教学调整提供依据'
    ],
    accent: '#ff7a90',
    accentSoft: 'rgba(255, 122, 144, 0.22)',
    icon: <LineChartOutlined />
  },
  {
    id: 'homework',
    navLabel: '扫码收作业',
    eyebrow: '核心能力 06',
    title: '扫码收作业，让回收、登记和确认更轻一点',
    description:
      '围绕作业管理、查收、数据收集和活动扫码，把原本反复登记的动作做成可快速执行的现场流程。',
    value: '扫码回收',
    valueLabel: '作业、数据收集、活动签到统一收口',
    highlights: [
      '减少纸面登记和人工清点的重复动作',
      '适合收作业、收表单、收报名、收活动反馈',
      '把课堂里的高频小动作压缩成一步操作'
    ],
    accent: '#53d7ff',
    accentSoft: 'rgba(83, 215, 255, 0.2)',
    icon: <QrcodeOutlined />
  },
  {
    id: 'attendance',
    navLabel: '考勤答题',
    eyebrow: '核心能力 07',
    title: '智能考勤与在线答题，把课堂互动做成闭环',
    description:
      '从智能考勤到在线答题、题库管理，系统把课堂状态确认和即时反馈放到同一条链路上，方便教师快速掌握班级节奏。',
    value: '课堂反馈',
    valueLabel: '考勤确认 + 在线答题 + 题库支撑',
    highlights: [
      '适合课堂签到、到课确认和即时检测',
      '题库与答题联动，帮助快速发起课堂互动',
      '与成绩、广播和班级管理自然衔接'
    ],
    accent: '#7ee081',
    accentSoft: 'rgba(126, 224, 129, 0.22)',
    icon: <ScheduleOutlined />
  },
  {
    id: 'sync',
    navLabel: '多端协同',
    eyebrow: '核心能力 08',
    title: '多端协同，把教师操作和教室现场真正打通',
    description:
      '管理后台负责配置和管理，微信小程序负责随手处理，桌面客户端承接教室现场展示，让不同角色在同一系统内完成协作。',
    value: '三端联动',
    valueLabel: '后台、小程序、教室桌面端协同运行',
    highlights: [
      '教室电脑承接展示，教师手机负责随时处理',
      '后台适合系统配置、组织管理和数据沉淀',
      '同一套功能逻辑在不同终端保持一致体验'
    ],
    accent: '#74a8ff',
    accentSoft: 'rgba(116, 168, 255, 0.22)',
    icon: <ApiOutlined />
  }
];

function OverviewVisual() {
  return (
    <div className="tour-scene tour-scene-overview">
      <div className="tour-window">
        <div className="tour-window-topbar">
          <span />
          <span />
          <span />
          <div className="tour-window-label">教室小喇叭 · 首页导览</div>
        </div>
        <div className="tour-overview-shell">
          <aside className="tour-overview-rail">
            <div className="tour-rail-brand">功能矩阵</div>
            <div className="tour-rail-item active">实时广播</div>
            <div className="tour-rail-item">AI 助手</div>
            <div className="tour-rail-item">成长记录</div>
            <div className="tour-rail-item">宠物积分</div>
            <div className="tour-rail-item">成绩分析</div>
          </aside>
          <div className="tour-overview-grid">
            <div className="tour-overview-card hero">
              <span>课堂触达</span>
              <strong>广播、提醒、场景切换</strong>
            </div>
            <div className="tour-overview-card">
              <span>AI 升级</span>
              <strong>个人助手与学校算力池</strong>
            </div>
            <div className="tour-overview-card">
              <span>成长闭环</span>
              <strong>积分、宠物、成长记录</strong>
            </div>
            <div className="tour-overview-card">
              <span>结果沉淀</span>
              <strong>成绩、作业、在线答题</strong>
            </div>
          </div>
        </div>
      </div>
      <div className="tour-floating-note left">班级管理</div>
      <div className="tour-floating-note right">多端协同</div>
    </div>
  );
}

function BroadcastVisual() {
  return (
    <div className="tour-scene tour-scene-broadcast">
      <div className="tour-phone-panel">
        <div className="tour-phone-notch" />
        <div className="tour-phone-body">
          <div className="tour-panel-title">微信小程序</div>
          <div className="tour-phone-card">
            <div className="tour-card-top">
              <span>广播内容</span>
              <span className="live">语音播报</span>
            </div>
            <strong>请同学们准备上交数学作业</strong>
            <div className="tour-chip-row">
              <span>顶部横幅</span>
              <span>全屏提示</span>
            </div>
          </div>
          <button type="button" className="tour-send-button">
            立即发送
          </button>
        </div>
      </div>

      <div className="tour-broadcast-link">
        <div className="tour-broadcast-wave" />
        <div className="tour-broadcast-wave second" />
        <span>实时送达</span>
      </div>

      <div className="tour-classroom-panel">
        <div className="tour-classroom-banner">703 教室已接收广播</div>
        <div className="tour-classroom-body">
          <div className="tour-screen-alert">
            <SoundOutlined />
            <strong>王老师：请同学们准备上交数学作业</strong>
          </div>
          <div className="tour-screen-grid">
            <div>桌面客户端自动弹出通知</div>
            <div>支持横幅 / 弹窗 / 全屏</div>
            <div>适配教室电脑日常待机场景</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AiVisual() {
  return (
    <div className="tour-scene tour-scene-ai">
      <div className="tour-ai-header">
        <span className="tour-badge">AI 助教</span>
        <span className="tour-badge ghost">学校算力池</span>
        <span className="tour-badge ghost">会员中心</span>
      </div>
      <div className="tour-ai-stack">
        <div className="tour-ai-card prompt">
          <div className="tour-panel-title">教师任务</div>
          <strong>请根据本周课堂情况生成一段班级反馈</strong>
          <p>同时提取值得关注的学生表现，并给出家校沟通建议。</p>
        </div>
        <div className="tour-ai-card response">
          <div className="tour-panel-title">AI 输出</div>
          <ul>
            <li>课堂参与度整体稳定，后排学生互动需加强</li>
            <li>建议对两位学生补充鼓励性反馈与家校沟通</li>
            <li>可直接生成班级总结、个别提醒和后续任务</li>
          </ul>
        </div>
        <div className="tour-ai-card stats">
          <div className="tour-stat-box">
            <span>Token 配置</span>
            <strong>按套餐可管理</strong>
          </div>
          <div className="tour-stat-box">
            <span>模型能力</span>
            <strong>学校统一分配</strong>
          </div>
        </div>
      </div>
    </div>
  );
}

function GrowthVisual() {
  return (
    <div className="tour-scene tour-scene-growth">
      <div className="tour-student-card">
        <div className="tour-avatar-ring">李</div>
        <div>
          <div className="tour-panel-title">学生成长档案</div>
          <strong>李同学 · 七年级四班</strong>
          <p>课堂表现、家访记录、表扬与提醒集中沉淀。</p>
        </div>
      </div>
      <div className="tour-growth-timeline">
        <div className="tour-growth-item positive">
          <span>表扬记录</span>
          <strong>课堂发言积极，主动帮助同学</strong>
        </div>
        <div className="tour-growth-item">
          <span>家访沟通</span>
          <strong>已与家长同步本周学习状态</strong>
        </div>
        <div className="tour-growth-item warn">
          <span>行为提醒</span>
          <strong>午休后需提升作业提交及时性</strong>
        </div>
      </div>
    </div>
  );
}

function PetVisual() {
  return (
    <div className="tour-scene tour-scene-pet">
      <div className="tour-pet-main">
        <div className="tour-pet-planet" />
        <div className="tour-pet-body">
          <div className="tour-pet-face">
            <span />
            <span />
          </div>
          <div className="tour-pet-mouth" />
        </div>
        <div className="tour-pet-level">LV.12</div>
      </div>
      <div className="tour-pet-side">
        <div className="tour-panel-title">班级积分榜</div>
        <div className="tour-rank-row top">
          <span>1</span>
          <strong>七年级四班</strong>
          <em>860 分</em>
        </div>
        <div className="tour-rank-row">
          <span>2</span>
          <strong>七年级二班</strong>
          <em>820 分</em>
        </div>
        <div className="tour-rank-row">
          <span>3</span>
          <strong>七年级一班</strong>
          <em>801 分</em>
        </div>
        <div className="tour-chip-row">
          <span>积分累计</span>
          <span>宠物成长</span>
          <span>排行反馈</span>
        </div>
      </div>
    </div>
  );
}

function GradesVisual() {
  return (
    <div className="tour-scene tour-scene-grades">
      <div className="tour-chart-shell">
        <div className="tour-panel-title">班级成绩走势</div>
        <div className="tour-chart-columns">
          <div className="tour-chart-column a" />
          <div className="tour-chart-column b" />
          <div className="tour-chart-column c" />
          <div className="tour-chart-column d" />
          <div className="tour-chart-column e" />
        </div>
        <div className="tour-chart-line">
          <span />
          <span />
          <span />
          <span />
        </div>
      </div>
      <div className="tour-grade-summary">
        <div className="tour-grade-card">
          <span>平均分</span>
          <strong>稳步提升</strong>
        </div>
        <div className="tour-grade-card">
          <span>班级对比</span>
          <strong>支持复盘</strong>
        </div>
        <div className="tour-grade-card">
          <span>结果联动</span>
          <strong>可结合成长记录</strong>
        </div>
      </div>
    </div>
  );
}

function HomeworkVisual() {
  return (
    <div className="tour-scene tour-scene-homework">
      <div className="tour-qr-card">
        <QrcodeOutlined />
        <strong>扫码提交 / 收取</strong>
        <span>适合作业、活动和数据收集</span>
      </div>
      <div className="tour-homework-list">
        <div className="tour-homework-row done">
          <span>数学作业</span>
          <strong>已回收 43 份</strong>
        </div>
        <div className="tour-homework-row live">
          <span>活动报名</span>
          <strong>扫码持续进入中</strong>
        </div>
        <div className="tour-homework-row">
          <span>课堂反馈</span>
          <strong>自动汇总结果</strong>
        </div>
      </div>
      <div className="tour-scan-track">
        <div className="tour-scan-dot" />
        <div className="tour-scan-dot" />
        <div className="tour-scan-dot" />
      </div>
    </div>
  );
}

function AttendanceVisual() {
  return (
    <div className="tour-scene tour-scene-attendance">
      <div className="tour-attendance-panel">
        <div className="tour-panel-title">智能考勤</div>
        <div className="tour-attendance-grid">
          <span className="on">到课</span>
          <span className="on">到课</span>
          <span>请假</span>
          <span className="on">到课</span>
          <span className="late">迟到</span>
          <span className="on">到课</span>
        </div>
      </div>
      <div className="tour-quiz-panel">
        <div className="tour-panel-title">在线答题 / 题库</div>
        <div className="tour-quiz-bar">
          <div className="tour-quiz-fill" />
        </div>
        <div className="tour-quiz-points">
          <span>即时检测</span>
          <span>课堂互动</span>
          <span>结果统计</span>
        </div>
      </div>
    </div>
  );
}

function SyncVisual() {
  return (
    <div className="tour-scene tour-scene-sync">
      <div className="tour-device desktop">
        <DesktopOutlined />
        <strong>教室桌面端</strong>
        <span>负责现场显示与广播承接</span>
      </div>
      <div className="tour-device tablet">
        <AppstoreOutlined />
        <strong>管理后台</strong>
        <span>负责配置、管理与数据沉淀</span>
      </div>
      <div className="tour-device mobile">
        <WechatOutlined />
        <strong>微信小程序</strong>
        <span>负责教师随手处理和即时操作</span>
      </div>
      <div className="tour-sync-curve" />
    </div>
  );
}

function FeatureVisual({ slideId }) {
  switch (slideId) {
    case 'overview':
      return <OverviewVisual />;
    case 'broadcast':
      return <BroadcastVisual />;
    case 'ai':
      return <AiVisual />;
    case 'growth':
      return <GrowthVisual />;
    case 'pet':
      return <PetVisual />;
    case 'grades':
      return <GradesVisual />;
    case 'homework':
      return <HomeworkVisual />;
    case 'attendance':
      return <AttendanceVisual />;
    case 'sync':
      return <SyncVisual />;
    default:
      return null;
  }
}

const HomePage = () => {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const [siteVisits, setSiteVisits] = useState(null);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowRight') {
        setActiveIndex((current) => Math.min(current + 1, SLIDES.length - 1));
      }

      if (event.key === 'ArrowLeft') {
        setActiveIndex((current) => Math.max(current - 1, 0));
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    let active = true;

    const getSessionId = () => {
      const storageKey = 'xlb-public-visit-session-id';

      try {
        const existing = window.sessionStorage.getItem(storageKey);
        if (existing) return existing;

        const created = window.crypto?.randomUUID?.()
          || `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        window.sessionStorage.setItem(storageKey, created);
        return created;
      } catch (error) {
        return `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      }
    };

    const recordVisitOnce = async () => {
      const recordedKey = 'xlb-public-product-visit-recorded';
      let shouldRecord = true;

      try {
        shouldRecord = window.sessionStorage.getItem(recordedKey) !== 'done';
        if (shouldRecord) window.sessionStorage.setItem(recordedKey, 'pending');
      } catch (error) {
        // 浏览器禁用会话存储时仍允许记录访问。
      }

      if (!shouldRecord) return;

      try {
        const response = await fetch('/api/v2/statistics/visit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            pagePath: '/product',
            pageTitle: '教室小喇叭产品首页',
            referrer: document.referrer || '',
            sessionId: getSessionId()
          })
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        try {
          window.sessionStorage.setItem(recordedKey, 'done');
        } catch (error) {
          // 统计成功即可，会话存储不可用不影响页面。
        }
      } catch (error) {
        try {
          window.sessionStorage.removeItem(recordedKey);
        } catch (storageError) {
          // 忽略存储异常，首页仍可正常展示。
        }
      }
    };

    const loadVisits = async () => {
      await recordVisitOnce();

      try {
        const response = await fetch('/api/v2/statistics/stats?days=365', { cache: 'no-store' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const result = await response.json();
        const total = Number(result?.data?.totalVisits ?? result?.totalVisits);
        if (active && Number.isFinite(total)) setSiteVisits(total);
      } catch (error) {
        if (active) setSiteVisits(null);
      }
    };

    loadVisits();

    return () => {
      active = false;
    };
  }, []);

  return (
    <Layout className="home-tour-page">
      <PublicHeader />

      <Content className="home-tour-content">
        <section
          className="home-tour-shell"
          style={{
            '--tour-accent': SLIDES[activeIndex].accent,
            '--tour-accent-soft': SLIDES[activeIndex].accentSoft
          }}
        >
          <div className="home-tour-ambient ambient-a" />
          <div className="home-tour-ambient ambient-b" />

          <div
            className="home-tour-track"
            style={{
              transform: `translateX(-${activeIndex * 100}%)`
            }}
          >
            {SLIDES.map((slide, index) => (
              <article
                key={slide.id}
                className="home-tour-slide"
                style={{
                  '--tour-accent': slide.accent,
                  '--tour-accent-soft': slide.accentSoft
                }}
              >
                <div className="home-tour-slide-inner">
                  <div className="home-tour-copy">
                    <div className="home-tour-copy-top">
                      <span className="home-tour-eyebrow">{slide.eyebrow}</span>
                      <div className="home-tour-icon">{slide.icon}</div>
                    </div>

                    <h1>{slide.title}</h1>
                    <Paragraph className="home-tour-description">{slide.description}</Paragraph>

                    <div className="home-tour-value-box">
                      <strong>{slide.value}</strong>
                      <span>{slide.valueLabel}</span>
                    </div>

                    <div className="home-tour-highlight-list">
                      {slide.highlights.map((item) => (
                        <div key={item} className="home-tour-highlight">
                          <CheckCircleFilled />
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>

                    <div className="home-tour-actions">
                      <Button type="primary" size="large" onClick={() => navigate('/login')}>
                        立即体验
                      </Button>
                      <Button size="large" onClick={() => navigate('/guide')}>
                        查看使用指南
                      </Button>
                    </div>

                    <div className="home-tour-index">
                      <Text>{String(index + 1).padStart(2, '0')}</Text>
                      <span>/ {String(SLIDES.length).padStart(2, '0')}</span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%' }}>
                    <div className="home-tour-wechat-banner">
                      <WechatOutlined className="home-tour-wechat-icon" />
                      <Text>
                        关注微信公众号“<strong>教室小喇叭</strong>”，回复“<strong>下载</strong>”，即可获得下载地址
                      </Text>
                    </div>
                    <div className="home-tour-visual-wrap">
                      <FeatureVisual slideId={slide.id} />
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>

          <div className="home-tour-footer-nav">
            <div className="home-tour-arrow-group">
              <button
                type="button"
                className="home-tour-arrow"
                onClick={() => setActiveIndex((current) => Math.max(current - 1, 0))}
                disabled={activeIndex === 0}
                aria-label="上一页"
              >
                <ArrowLeftOutlined />
              </button>
              <button
                type="button"
                className="home-tour-arrow primary"
                onClick={() =>
                  setActiveIndex((current) => Math.min(current + 1, SLIDES.length - 1))
                }
                disabled={activeIndex === SLIDES.length - 1}
                aria-label="下一页"
              >
                <span>下一页</span>
                <ArrowRightOutlined />
              </button>
              <div className="home-tour-visit-count" aria-label="网站访问次数">
                <EyeOutlined />
                <span>网站访问</span>
                <strong>{siteVisits === null ? '--' : siteVisits.toLocaleString('zh-CN')}</strong>
                <span>次</span>
              </div>
            </div>

            <div className="home-tour-pagination">
              {SLIDES.map((slide, index) => (
                <button
                  key={slide.id}
                  type="button"
                  className={`home-tour-dot${index === activeIndex ? ' active' : ''}`}
                  onClick={() => setActiveIndex(index)}
                >
                  <span className="home-tour-dot-index">{index + 1}</span>
                  <span className="home-tour-dot-label">{slide.navLabel}</span>
                </button>
              ))}
            </div>
          </div>
        </section>

        <PublicFooter />
      </Content>
    </Layout>
  );
};

export default HomePage;
